import React from 'react';
import './ui.css';

/*
 * One row per record, sat on the page rather than inside a bordered table.
 *
 * Used where the app currently renders either a w3-table or a run of tags —
 * registered players, club members. A table draws 15 vertical rules to line
 * up 4 pieces of information; this draws one hairline between records and
 * lets alignment come from the columns.
 *
 * The header and the rows are given the SAME `columns` array, which is what
 * stops them drifting apart — the usual failure of hand-built list layouts,
 * where a column is added to one and not the other.
 *
 * A column is:
 *   key      unique within the array
 *   label    header text; omit for checkbox and action columns
 *   render   (item) => node. Omit to read item[key].
 *   width    fixed px width. Omit for the column that should take the slack.
 *   grow     true for a slack-taking column. More than one may grow, and
 *            they share what is left over — on a wide screen a single
 *            growing column swallows all of it and leaves a gap.
 *   primary  the one column that identifies the record. Sets the reading
 *            size, and is the one that gets a line of its own on a phone.
 *   minWidth floor for a growing column, so it is not crushed to nothing
 *   maxWidth ceiling for a growing column. A select showing the word
 *            "player" gains nothing from being 700px wide, and on a very
 *            wide monitor that is exactly what growth gives it.
 *   align    'center' | 'right'; defaults to left
 *   collapse false to keep the column inline on a phone
 */

/* Widths and alignment are per-column data, so they have to be inline. Growth
   deliberately is NOT: an inline flex-basis would outrank the stylesheet, and
   the phone layout needs to override it to give the name a line of its own. */
function cellStyle(col) {
  return {
    ...(col.width ? { width: col.width, flexShrink: 0 } : {}),
    // The growing column gets whatever the fixed ones leave, which in a wide
    // list is close to nothing. A floor stops names breaking mid-word.
    ...(col.minWidth ? { minWidth: col.minWidth } : {}),
    ...(col.maxWidth ? { maxWidth: col.maxWidth } : {}),
    ...(col.align === 'center' ? { display: 'flex', justifyContent: 'center' } : {}),
    ...(col.align === 'right' ? { display: 'flex', justifyContent: 'flex-end' } : {}),
  };
}

function cellClass(col, extra = '') {
  return `gs-datarow-cell${col.grow ? ' gs-datarow-grow' : ''}${extra}`;
}

export function DataRowHeader({ columns = [], style }) {
  return (
    <div className="gs-datarow-head" style={style}>
      {columns.map(col => (
        <div key={col.key} className={cellClass(col)} style={cellStyle(col)}>
          {col.label || ''}
        </div>
      ))}
    </div>
  );
}

function DataRow({ columns = [], item, onClick, style }) {
  // A row is usually clickable AND contains its own controls — a paid
  // toggle, a remove button. Without this, ticking paid would also open the
  // player. Anything that handles its own click swallows the row's.
  const handleClick = e => {
    if (!onClick) return;
    if (e.target.closest('button, a, input, select, textarea, label, [role="switch"]')) return;
    onClick(e);
  };

  return (
    <div
      className={`gs-datarow${onClick ? ' gs-datarow-clickable' : ''}`}
      style={style}
      onClick={handleClick}
      // A clickable row has to be reachable and operable without a mouse.
      // Rows with no onClick stay plain divs rather than empty tab stops.
      {...(onClick ? {
        role: 'button',
        tabIndex: 0,
        onKeyDown: e => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); }
        },
      } : {})}>
      {columns.map(col => (
        <div
          key={col.key}
          className={cellClass(col, col.primary ? ' gs-datarow-primary' : '')}
          style={cellStyle(col)}
          // The identifying column keeps its width on a phone — it is the one
          // that gets a line to itself, not one of the ones tucked under.
          data-gs-collapse={col.primary || col.collapse === false ? 'false' : 'true'}>
          {col.render ? col.render(item) : item?.[col.key]}
        </div>
      ))}
    </div>
  );
}

export default DataRow;
